import { Vendor } from '@kalos/kalos-rpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
  ScrollArea,
  toast,
} from '@kalos/ui';
import { Search, Trash2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useVendorBatchQuery,
  useVendorCreateMutation,
  useVendorDeleteMutation,
  useVendorUpdateMutation,
} from '../../../../hooks/react-query/useVendorQuery';
import { getNetTermsBadgeVariant, getPaymentTermsDisplayText } from './constants/paymentTerms';
import { VendorCrudForm } from './VendorForm';

interface VendorSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (vendor: Vendor) => void;
  selectedVendorId?: number;
}

const VendorItem: FC<{
  vendor: Vendor;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: (vendor: Vendor) => void;
  onEdit: (vendor: Vendor) => void;
  onDelete: (vendor: Vendor) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({
  vendor,
  isSelected,
  isFocused,
  onSelect,
  onEdit,
  onDelete,
  canDelete = true,
  canEdit = true,
}) => {
  const handleClick = useCallback(() => {
    onSelect(vendor);
  }, [onSelect, vendor]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(vendor);
      }
    },
    [onSelect, vendor],
  );

  return (
    <div
      className={`hover:bg-muted/50 flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-border'
      } ${isFocused ? 'ring-primary ring-2 ring-offset-2' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
    >
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{vendor.vendorName}</h3>
          <Badge variant={getNetTermsBadgeVariant(vendor.netTerms || 0)}>
            {getPaymentTermsDisplayText(vendor.netTerms)}
          </Badge>
        </div>
        {vendor.vendorAddress && (
          <p className="text-muted-foreground text-sm">
            {[vendor.vendorAddress, vendor.vendorCity, vendor.vendorState, vendor.vendorZip]
              .filter(Boolean)
              .join(', ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(vendor);
            }}
            className="text-blue-600 hover:text-blue-700"
          >
            Edit
          </Button>
        )}
        {canDelete && vendor.id !== 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(vendor);
            }}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const VendorSelectionModal: FC<VendorSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  selectedVendorId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingVendor, setIsAddingVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const modalRef = useRef<HTMLDivElement>(null);

  const vendorsQuery = useVendorBatchQuery({
    filter: {
      isActive: 1,
      withoutLimit: true,
      orderBy: 'vendor_name',
    },
  });

  const createVendorMutation = useVendorCreateMutation();
  const updateVendorMutation = useVendorUpdateMutation();
  const deleteVendorMutation = useVendorDeleteMutation();

  const filteredVendors = useMemo(() => {
    if (!vendorsQuery.data?.results) return [];

    const searchLower = searchTerm.toLowerCase();
    return vendorsQuery.data.results.filter(
      (vendor) =>
        vendor.vendorName.toLowerCase().includes(searchLower) ||
        (vendor.vendorAddress && vendor.vendorAddress.toLowerCase().includes(searchLower)),
    );
  }, [vendorsQuery.data?.results, searchTerm]);

  // All selectable vendors including "Unselected" option
  const allSelectableVendors = useMemo(() => {
    const unselectedVendor = Vendor.create({ id: 0, vendorName: 'Unselected', isActive: 1 });
    return [unselectedVendor, ...filteredVendors];
  }, [filteredVendors]);

  const handleSelectVendor = useCallback((vendor: Vendor) => {
    setSelectedVendor(vendor);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSearchTerm('');
    setIsAddingVendor(false);
    setEditingVendor(null);
    setVendorToDelete(null);
    setSelectedVendor(null);
    setFocusedIndex(-1);
    onClose();
  }, [onClose]);

  const handleConfirmSelection = useCallback(() => {
    if (selectedVendor) {
      onSelect(selectedVendor);
      onClose();
    } else {
      toast({
        variant: 'destructive',
        title: 'No vendor selected',
        description: 'Please select a vendor before clicking SELECT.',
      });
    }
  }, [selectedVendor, onSelect, onClose]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isAddingVendor || editingVendor) return; // Don't handle keys when forms are open

      // Check if user is typing in an input field
      const isTypingInInput =
        event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev < allSelectableVendors.length - 1 ? prev + 1 : 0;
            return nextIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : allSelectableVendors.length - 1;
            return nextIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < allSelectableVendors.length) {
            const focusedVendor = allSelectableVendors[focusedIndex];
            if (selectedVendor?.id === focusedVendor.id) {
              // If the focused vendor is already selected, commit the selection
              handleConfirmSelection();
            } else {
              // Otherwise, select the focused vendor
              setSelectedVendor(focusedVendor);
            }
          } else if (selectedVendor) {
            // If no vendor is focused but one is selected, commit the selection
            handleConfirmSelection();
          }
          break;
        case ' ':
          // Allow spaces when typing in the search input
          if (isTypingInInput) {
            return;
          }
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < allSelectableVendors.length) {
            const focusedVendor = allSelectableVendors[focusedIndex];
            setSelectedVendor(focusedVendor);
          }
          break;
        case 'Escape':
          event.preventDefault();
          handleCloseModal();
          break;
      }
    },
    [
      isAddingVendor,
      editingVendor,
      allSelectableVendors,
      focusedIndex,
      selectedVendor,
      handleConfirmSelection,
      handleCloseModal,
    ],
  );

  const handleAddVendor = useCallback(() => {
    setIsAddingVendor(true);
  }, []);

  const handleEditVendor = useCallback((vendor: Vendor) => {
    setEditingVendor(vendor);
  }, []);

  const handleDeleteVendor = useCallback((vendor: Vendor) => {
    setVendorToDelete(vendor);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!vendorToDelete) return;

    try {
      await deleteVendorMutation.mutateAsync(vendorToDelete.id);
      toast({
        title: 'Vendor deleted',
        description: `${vendorToDelete.vendorName} has been deleted successfully.`,
      });
      setVendorToDelete(null);
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      toast({
        variant: 'destructive',
        title: 'Error deleting vendor',
        description: 'Failed to delete vendor. Please try again.',
      });
    }
  }, [vendorToDelete, deleteVendorMutation]);

  const handleSaveVendor = useCallback(
    async (vendor: Vendor) => {
      if (vendor.id) {
        // Update existing vendor
        await updateVendorMutation.mutateAsync(vendor);
        toast({
          title: 'Vendor updated',
          description: `${vendor.vendorName} has been updated successfully.`,
        });
      } else {
        // Create new vendor
        const newVendor = await createVendorMutation.mutateAsync(vendor);
        toast({
          title: 'Vendor created',
          description: `${vendor.vendorName} has been created successfully.`,
        });
        // Auto-select the newly created vendor
        setSelectedVendor(newVendor || null);
      }

      // Close the CRUD form but keep the main modal open
      setIsAddingVendor(false);
      setEditingVendor(null);

      // The vendor list will automatically refresh naturally via React Query
      // DO NOT trigger any transaction-related callbacks or close the main modal
    },
    [createVendorMutation, updateVendorMutation],
  );

  const handleCloseCrudForm = useCallback(() => {
    setIsAddingVendor(false);
    setEditingVendor(null);
  }, []);

  const handleSelectExistingVendor = useCallback(
    (vendor: Vendor) => {
      onSelect(vendor);
      handleCloseModal();
    },
    [onSelect, handleCloseModal],
  );

  // Set initial selection based on selectedVendorId
  useMemo(() => {
    if (selectedVendorId && vendorsQuery.data?.results) {
      const vendor = vendorsQuery.data.results.find((v) => v.id === selectedVendorId);
      if (vendor) {
        setSelectedVendor(vendor);
      } else if (selectedVendorId === 0) {
        setSelectedVendor(Vendor.create({ id: 0, vendorName: 'Unselected', isActive: 1 }));
      }
    }
  }, [selectedVendorId, vendorsQuery.data?.results]);

  // Reset focus when modal opens
  useEffect(() => {
    if (open) {
      setFocusedIndex(-1);
      // Focus the modal container for keyboard navigation
      setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseModal}>
        <DialogContent
          className="max-h-[80vh] max-w-2xl"
          ref={modalRef}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <div className="space-y-4">
            {/* Header with Action Buttons */}
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">Vendors</DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  className="bg-red-400 text-white hover:bg-red-500"
                  onClick={handleConfirmSelection}
                >
                  SELECT
                </Button>
                <Button variant="outline" onClick={handleAddVendor}>
                  ADD VENDOR
                </Button>
                <Button variant="outline" onClick={handleCloseModal}>
                  CLOSE
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search Vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Vendor List */}
            <ScrollArea className="h-96">
              <div className="space-y-3" role="listbox" aria-label="Vendor selection">
                {vendorsQuery.isPending || vendorsQuery.isFetching ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-muted-foreground">
                      {vendorsQuery.isPending ? 'Loading vendors...' : 'Refreshing vendors...'}
                    </div>
                  </div>
                ) : allSelectableVendors.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-muted-foreground">
                      {searchTerm
                        ? 'No vendors found matching your search.'
                        : 'No vendors available.'}
                    </div>
                  </div>
                ) : (
                  allSelectableVendors.map((vendor, index) => (
                    <VendorItem
                      key={vendor.id}
                      vendor={vendor}
                      isSelected={selectedVendor?.id === vendor.id}
                      isFocused={focusedIndex === index}
                      onSelect={handleSelectVendor}
                      onEdit={vendor.id === 0 ? () => {} : handleEditVendor}
                      onDelete={vendor.id === 0 ? () => {} : handleDeleteVendor}
                      canDelete={vendor.id !== 0}
                      canEdit={vendor.id !== 0}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* CRUD Forms */}
      <VendorCrudForm
        open={isAddingVendor}
        onClose={handleCloseCrudForm}
        onSave={handleSaveVendor}
        onSelectExisting={handleSelectExistingVendor}
        isLoading={createVendorMutation.isPending}
        existingVendors={vendorsQuery.data?.results || []}
      />

      <VendorCrudForm
        open={!!editingVendor}
        onClose={handleCloseCrudForm}
        onSave={handleSaveVendor}
        onSelectExisting={handleSelectExistingVendor}
        vendor={editingVendor}
        isLoading={updateVendorMutation.isPending}
        existingVendors={vendorsQuery.data?.results || []}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!vendorToDelete} onOpenChange={() => setVendorToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{vendorToDelete?.vendorName}&rdquo;? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteVendorMutation.isPending}
            >
              {deleteVendorMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
